import type { IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router';
import { ApiError } from './api/http';
import { AuthGate } from './auth/AuthGate';
import { TenantGate } from './auth/TenantGate';
import { useHasFullAccess, useIsAdministrator } from './auth/useCallerContext';
import { AppLayout } from './components/AppLayout';
import { LoadingBlock } from './components/QueryStateOverlay';
import { LanguageProvider } from './i18n/LanguageProvider';
import { AccountsPage as AdminAccountsPage } from './pages/admin/AccountsPage';
import { AdminPage } from './pages/admin/AdminPage';
import { CodelistsPage } from './pages/codelists/CodelistsPage';
import { CommunicationLogDetailPage } from './pages/communication/CommunicationLogDetailPage';
import { CommunicationLogPage } from './pages/communication/CommunicationLogPage';
import { TenantDetailPage } from './pages/admin/TenantDetailPage';
import { FilesPage } from './pages/files/FilesPage';
import { FlowisPage } from './pages/flowis/FlowisPage';
import { MappingsPage } from './pages/mappings/MappingsPage';
import { PeppolCheckPage } from './pages/peppol/PeppolCheckPage';
import { OperationDetailPage } from './pages/operations/OperationDetailPage';
import { OperationsPage } from './pages/operations/OperationsPage';
import { SubmitPage } from './pages/submit/SubmitPage';
import { TenantProvider } from './tenant/TenantProvider';
import { ColorModeProvider } from './theme/ColorModeProvider';

// Split into its own chunk and only ever mounted in development, so a production visit never
// downloads it.
const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((module) => ({ default: module.ReactQueryDevtools })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // A refused or missing resource will not become available by asking again.
      retry: (failureCount, error) =>
        !(error instanceof ApiError && error.status >= 400 && error.status < 500) && failureCount < 2,
    },
  },
});

/**
 * Administration takes an Entra app role the API checks for itself. The nav already hides the entry;
 * this turns a typed-in or bookmarked URL away too, rather than letting it render into a 403.
 */
function AdministratorOnly() {
  return useIsAdministrator() ? <Outlet /> : <Navigate to="/files" replace />;
}

/**
 * The sections a bare `registration` submitter may not reach. The nav already hides them; this
 * turns a typed-in or bookmarked URL away too, rather than letting it render into a 403.
 */
function FullAccessOnly() {
  return useHasFullAccess() ? <Outlet /> : <Navigate to="/submit" replace />;
}

/**
 * Where "/" (and any unknown path) lands. An administrator starts in administration — it answers
 * without a tenant, so signing in never stops at tenant selection. A bare registration submitter
 * starts on the one section they have; everyone else on their files.
 */
function DefaultLanding() {
  const isAdministrator = useIsAdministrator();
  const hasFullAccess = useHasFullAccess();

  return <Navigate to={isAdministrator ? '/admin' : hasFullAccess ? '/files' : '/submit'} replace />;
}

export function App({ instance }: { instance: IPublicClientApplication }) {
  return (
    <MsalProvider instance={instance}>
      {/* Above the theme: MUI's own translations are merged into the theme by language, so the
          provider that builds it has to be able to read the choice. */}
      <LanguageProvider>
        <ColorModeProvider>
          <QueryClientProvider client={queryClient}>
            <SnackbarProvider
              maxSnack={3}
              autoHideDuration={6000}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              {/* The router sits above the gates so TenantGate can tell where the user is heading:
                administration is exempt from tenant resolution server-side, and gating it here would
                make the very first tenant impossible to register. */}
              <BrowserRouter>
                <AuthGate>
                  <TenantGate>
                    <TenantProvider>
                      <Routes>
                        <Route element={<AppLayout />}>
                          <Route index element={<DefaultLanding />} />
                          <Route path="submit" element={<SubmitPage />} />
                          {/* The operation detail stays outside the guard: the submitter is sent
                              there after handing a document in, and the API allows them the read. */}
                          <Route path="operations/:operationGid" element={<OperationDetailPage />} />
                          <Route element={<FullAccessOnly />}>
                            <Route path="files" element={<FilesPage />} />
                            <Route path="operations" element={<OperationsPage />} />
                            <Route path="communication" element={<CommunicationLogPage />} />
                            <Route path="communication/:entryGid" element={<CommunicationLogDetailPage />} />
                            <Route path="flowis" element={<FlowisPage />} />
                            <Route path="peppol" element={<PeppolCheckPage />} />
                            <Route path="mappings" element={<MappingsPage />} />
                          </Route>
                          <Route element={<AdministratorOnly />}>
                            <Route path="admin" element={<AdminPage />} />
                            <Route path="admin/accounts" element={<AdminAccountsPage />} />
                            <Route path="admin/tenant/:tenantGid" element={<TenantDetailPage />} />
                            {/* Code lists are administration by right but a section of their own by
                                placement; the old address under /admin still lands there. */}
                            <Route path="codelists" element={<CodelistsPage />} />
                            <Route path="admin/codelists" element={<Navigate to="/codelists" replace />} />
                          </Route>
                          <Route path="*" element={<DefaultLanding />} />
                        </Route>
                      </Routes>
                    </TenantProvider>
                  </TenantGate>
                </AuthGate>
              </BrowserRouter>
            </SnackbarProvider>
            {import.meta.env.DEV && (
              <Suspense fallback={<LoadingBlock />}>
                <ReactQueryDevtools initialIsOpen={false} />
              </Suspense>
            )}
          </QueryClientProvider>
        </ColorModeProvider>
      </LanguageProvider>
    </MsalProvider>
  );
}
